import { GeocodeOptions, OutputFormat, RequestType, geocode, setDefaults } from "react-geocode";

import { getGoogleKey } from "utils";

import { COUNTRY_CODE, INPUT_STATE, InputState, SupportedCountryPostcodes } from "consts";

import { CropSaleData } from "models";

enum ADDRESS_COMPONENT_TYPES {
  ADMINISTRATIVE_AREA_LEVEL_1 = "administrative_area_level_1",
  POSTAL_CODE = "postal_code",
  COUNTRY = "country",
  POLITICAL = "political",
}
interface AddressComponent {
  long_name?: string;
  short_name?: string;
  types: ADDRESS_COMPONENT_TYPES[];
}

export const isUniquePurchaserName = (cropSales: CropSaleData[], name: string, currentIndex: number): boolean =>
  cropSales.every((sale, index) => index === currentIndex || sale.purchaser.name !== name);

export const isValidPercentageDecimal = (v: number) => v >= 0 && v <= 100;

export const isValidNonnegativeNumber = (v: number) => v >= 0;

export const isValidPositiveNumber = (v: number) => v > 0;

export const isNullOrUndefined = (v: unknown): v is null | undefined => v === undefined || v === null;

const UNBOUNDED: PracticeManagement.ParameterBounds = [-Infinity, -Infinity, Infinity, Infinity];

export const inputStateForValue = (v: number, [hardMin, softMin, softMax, hardMax]: PracticeManagement.ParameterBounds = UNBOUNDED): InputState => {
  if (v < hardMin) return INPUT_STATE.tooLowHard;
  if (v < softMin) return INPUT_STATE.tooLowSoft;
  if (v > hardMax) return INPUT_STATE.tooHighHard;
  if (v > softMax) return INPUT_STATE.tooHighSoft;
  return INPUT_STATE.expected;
};

export const isWithinBounds = (v: number, min: number, max: number) => v >= min && v <= max;

const validateNumber = (isValid: (v: number) => boolean, errorMsg: string) => (_: unknown, val: number | null | undefined) =>
  isNullOrUndefined(val) || isValid(val) ? Promise.resolve() : Promise.reject(new Error(errorMsg));

/** Form rule: empty or within [min, max]. */
export const numberRangeRule = (min: number, max: number, errorMsg: string) => ({ validator: validateNumber((v) => isWithinBounds(v, min, max), errorMsg) });

/** Form rule: empty or a percentage (0–100). */
export const percentRangeRule = (errorMsg: string) => ({ validator: validateNumber(isValidPercentageDecimal, errorMsg) });

/** Form rule: empty or ≥ 0. */
export const nonNegativeRule = (errorMsg: string) => ({ validator: validateNumber(isValidNonnegativeNumber, errorMsg) });

/** Form rule: required and > 0. */
export const positiveRequiredRule = (errorMsg: string) => ({
  validator: (_: unknown, val: number | null | undefined) =>
    !isNullOrUndefined(val) && isValidPositiveNumber(val) ? Promise.resolve() : Promise.reject(new Error(errorMsg)),
});

// Same soft/hard bound semantics as inputStateForValue, as a yes/no for callers that only need to flag an entry
// (and that may not have a value or a benchmark range yet).
export const isOutsideExpectedRange = (value: number | null | undefined, bounds: PracticeManagement.ParameterBounds | undefined): boolean =>
  value !== null && value !== undefined && !!bounds && inputStateForValue(value, bounds) !== INPUT_STATE.expected;

let isGeocodeInitialized = false;

const initializeGeocodeDefaults = (country?: string) => {
  if (isGeocodeInitialized) return;
  isGeocodeInitialized = true;

  setDefaults({
    key: getGoogleKey(),
    language: country ?? COUNTRY_CODE.GERMANY,
    region: country ?? COUNTRY_CODE.GERMANY,
    outputFormat: OutputFormat.JSON,
  });
};

const searchPostCodeOverGoogleMapsAPI = async ({ country, postCode }: { country: COUNTRY_CODE; postCode: string }) => {
  try {
    const options: GeocodeOptions = {
      components: `country:${country}`,
      outputFormat: OutputFormat.JSON,
    };

    const response = await geocode(RequestType.ADDRESS, postCode, options);
    if (response.status !== "OK") return null;

    const foundRegion: string = response.results[0].address_components.find(
      (component: AddressComponent) => component.types.indexOf(ADDRESS_COMPONENT_TYPES.ADMINISTRATIVE_AREA_LEVEL_1) >= 0
    )?.long_name;
    if (foundRegion) return foundRegion;

    const foundCountry: string = response.results[0].address_components.find(
      (component: AddressComponent) => component.types.includes(ADDRESS_COMPONENT_TYPES.COUNTRY) && component.types.includes(ADDRESS_COMPONENT_TYPES.POLITICAL)
    )?.short_name;
    const isPartialMatch = response.results[0].partial_match;

    return !isPartialMatch && foundCountry?.toLowerCase() === country.toLowerCase() ? foundCountry : null;
  } catch {
    return null;
  }
};

/**
 * @function searchPostCode
 * @description Looks up a region associated with a postal code for a given country.
 * @param postCode - The postal/ZIP code to search for.
 * @param country - The country code that scopes validation and lookup.
 * @returns The region returned by the Google Maps API, or `null` if unavailable.
 */
export const searchPostCode = async (postCode?: string | null, country?: COUNTRY_CODE | null) => {
  if (!postCode || !country) return null;

  initializeGeocodeDefaults(country);
  if (!(country in POSTCODE_PATTERNS)) return null;

  if (!isValidPostCodeFormat(postCode, country)) return null;

  const regionFromGoogleMapsAPI = await searchPostCodeOverGoogleMapsAPI({ country, postCode: postCode });
  return regionFromGoogleMapsAPI;
};

const POSTCODE_PATTERNS: Record<SupportedCountryPostcodes, RegExp> = {
  DE: /^\d{5}$/, // Germany: 5 digits (e.g., 10115)
  AT: /^\d{4}$/, // Austria: 4 digits (e.g., 2000)
  CH: /^\d{4}$/, // Switzerland: 4 digits (e.g., 8001)
  PL: /^\d{2}[\s-]?\d{3}$/, // Poland: XX-XXX format (e.g., 00-950)
  SK: /^\d{3}[\s-]?\d{2}$/, // Slovakia: XXX-XX format (e.g., 123-45)
  US: /^\d{5}(-\d{4})?$/, // USA: 5 digits or ZIP+4 (e.g., 12345 or 12345-6789)
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, // GB: Various formats (e.g., SW1A 1AA, M1 1AA)
  CZ: /^\d{3}\s?\d{2}$/, // Czechia: 3 digits space 2 digits (e.g., 123 45) Flexible space optional
  HU: /^\d{4}$/, // Hungary: 4 digits (e.g., 1234)
};

/**
 * @function isValidPostCodeFormat
 * @description Validates a post code against a regex for the provided country
 * @param postCode - The postal/ZIP code to search for.
 * @param country - The country code that scopes validation and lookup.
 * @returns True/false if the post code matches that country's post code format
 */
export const isValidPostCodeFormat = (postCode?: string, country?: COUNTRY_CODE | null): boolean => {
  if (!postCode || !country) {
    return false;
  }

  const countryCode = country.toString().toUpperCase() as SupportedCountryPostcodes;

  const pattern = POSTCODE_PATTERNS[countryCode];

  if (!pattern) {
    return false;
  }

  return pattern.test(postCode.trim());
};
